import React, { useEffect, useState } from "react";
import { useTranslation } from "../contexts/LocaleContext";

const AddUserModal = ({
  isOpen,
  onClose,
  onSubmit,
  isLoading,
  initialData = null,
}) => {
  const { t } = useTranslation();
  const [showPassword, setShowPassword] = useState(false);
  const [form, setForm] = useState({
    id: "",
    userName: "",
    passWord: "",
    NickName: "",
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isOpen) {
      const src = initialData || {};
      setForm({
        id: src.id || "",
        userName: src.userName || "",
        passWord: src.passWord || "",
        // support different API shapes: NickName or nickName
        NickName: src.NickName ?? src.nickName ?? src.Nickname ?? "",
      });
      setErrors({});
    }
    // Only re-run when open state or explicit initialData prop changes
  }, [isOpen, initialData]);

  if (!isOpen) return null;

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const validate = () => {
    const e = {};
    if (!form.userName)
      e.userName = t
        ? t("user.modal.account_required") || "Account is required"
        : "Account is required";
    if (!form.passWord)
      e.passWord = t
        ? t("user.modal.password_required") || "Password is required"
        : "Password is required";
    return e;
  };

  const handleSubmit = async (ev) => {
    if (ev && ev.preventDefault) ev.preventDefault();
    const e = validate();
    setErrors(e);
    if (Object.keys(e).length > 0) return;
    if (onSubmit) {
      await onSubmit(form);
    }
  };

  // Nickname is optional; only require account and password
  const isFormValid = !!(form.userName && form.passWord);

  return (
    <div className="modal-overlay" role="dialog" aria-modal="true">
      <div className="modal-card">
        <div className="modal-header">
          <h3>
            {initialData
              ? t
                ? t("user.modal.edit") || "Edit User"
                : "Edit User"
              : t
              ? t("user.modal.add")
              : "Add a User"}
          </h3>
          <button className="modal-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        <form className="modal-body" onSubmit={handleSubmit}>
          <div className="form-row">
            <label>
              <span className="label-icon" aria-hidden="true">
                <svg
                  t="1767855381740"
                  class="icon"
                  viewBox="0 0 1024 1024"
                  version="1.1"
                  xmlns="http://www.w3.org/2000/svg"
                  p-id="40242"
                  width="200"
                  height="200"
                >
                  <path
                    d="M672 320a160 160 0 1 0-160 160 160 160 0 0 0 160-160z m64 0A224 224 0 1 1 512 96a224 224 0 0 1 224 224z"
                    p-id="40243"
                    fill="#000000"
                  ></path>
                  <path
                    d="M224 832a32 32 0 0 1-64 0 352 352 0 0 1 704 0 32 32 0 0 1-64 0 288 288 0 0 0-576 0z"
                    p-id="40244"
                    fill="#000000"
                  ></path>
                </svg>
              </span>
              {t ? t("users.table.account") : "Account"}
            </label>
            <input
              type="text"
              value={form.userName}
              onChange={(e) => handleChange("userName", e.target.value)}
              placeholder={t ? t("users.table.account") : "Account"}
            />
            {errors.userName && (
              <div className="form-error">{errors.userName}</div>
            )}
          </div>

          <div className="form-row password-row">
            <label>
              <span className="label-icon" aria-hidden="true">
                <svg
                  t="1767855239372"
                  class="icon"
                  viewBox="0 0 1024 1024"
                  version="1.1"
                  xmlns="http://www.w3.org/2000/svg"
                  p-id="37218"
                  width="200"
                  height="200"
                >
                  <path
                    d="M418.133333 499.2c-21.333333-34.133333-29.866667-72.533333-29.866666-115.2 0-119.466667 93.866667-213.333333 213.333333-213.333333s213.333333 93.866667 213.333333 213.333333-93.866667 213.333333-213.333333 213.333333c-46.933333 0-85.333333-12.8-123.733333-38.4l-98.133334 98.133334 46.933334 46.933333-59.733334 59.733333-46.933333-46.933333-29.866667 29.866667 46.933334 46.933333L277.333333 853.333333 170.666667 746.666667l247.466666-247.466667zM601.6 256c-72.533333 0-128 55.466667-128 128s55.466667 128 128 128 128-55.466667 128-128-59.733333-128-128-128z"
                    fill="#000000"
                    p-id="37219"
                  ></path>
                </svg>
              </span>
              {t ? t("users.table.password") : "Password"}
            </label>
            <input
              type={showPassword ? "text" : "password"}
              value={form.passWord}
              onChange={(e) => handleChange("passWord", e.target.value)}
              placeholder={t ? t("users.table.password") : "Password"}
              className="password-input"
            />
            <button
              type="button"
              onClick={() => setShowPassword((s) => !s)}
              aria-label={showPassword ? "Hide password" : "Show password"}
              title={showPassword ? "Hide password" : "Show password"}
              className="password-toggle"
            >
              {showPassword ? (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  aria-hidden="true"
                >
                  <path
                    d="M17.94 17.94 6.06 6.06"
                    stroke="#000"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M10.58 10.58a2 2 0 1 0 2.84 2.84"
                    stroke="#000"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M2 12s4-7 10-7 10 7 10 7-4 7-10 7-10-7-10-7z"
                    stroke="#000"
                    strokeWidth="1.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              ) : (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  aria-hidden="true"
                >
                  <path
                    d="M2 12s4-7 10-7 10 7 10 7-4 7-10 7S2 12 2 12z"
                    stroke="#000"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <circle
                    cx="12"
                    cy="12"
                    r="3"
                    stroke="#000"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
            </button>
            {errors.passWord && (
              <div className="form-error">{errors.passWord}</div>
            )}
          </div>

          <div className="form-row">
            <label>
              <span className="label-icon" aria-hidden="true">
                <svg
                  t="1767855289301"
                  class="icon"
                  viewBox="0 0 1024 1024"
                  version="1.1"
                  xmlns="http://www.w3.org/2000/svg"
                  p-id="38299"
                  width="200"
                  height="200"
                >
                  <path
                    d="M512 426.666667c-106.666667 0-192-85.333333-192-192S405.333333 42.666667 512 42.666667s192 85.333333 192 192-85.333333 192-192 192z m0-298.666667c-59.733333 0-106.666667 46.933333-106.666667 106.666667S452.266667 341.333333 512 341.333333s106.666667-46.933333 106.666667-106.666666S571.733333 128 512 128zM85.333333 917.333333c-25.6 0-42.666667-17.066667-42.666666-42.666666 0-213.333333 192-384 426.666666-384 25.6 0 42.666667 17.066667 42.666667 42.666666s-17.066667 42.666667-42.666667 42.666667c-187.733333 0-341.333333 132.266667-341.333333 298.666667 0 25.6-17.066667 42.666667-42.666667 42.666666zM661.333333 938.666667h-85.333333l-42.666667-42.666667v-85.333333l12.8-29.866667 213.333334-213.333333h59.733333l85.333333 85.333333v59.733333l-213.333333 213.333334-29.866667 12.8z m-42.666666-85.333334h25.6l170.666666-170.666666-25.6-25.6-170.666666 170.666666v25.6z"
                    fill="#000000"
                    p-id="38300"
                  ></path>
                </svg>
              </span>
              {t ? t("users.table.nickname") : "Nickname"}
            </label>
            <input
              value={form.NickName}
              onChange={(e) => handleChange("NickName", e.target.value)}
              placeholder={t ? t("users.table.nickname") : "Nickname"}
            />
          </div>

          <div className="modal-actions">
            <button type="button" className="btn-ghost" onClick={onClose}>
              {t ? t("user.modal.cancel") : "Cancel"}
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={isLoading || !isFormValid}
            >
              {isLoading ? (
                <>
                  <span className="btn-spinner" aria-hidden="true" />
                </>
              ) : t ? (
                t("user.modal.save")
              ) : (
                "Save"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddUserModal;
